import React, { createContext, useContext, useState, useEffect } from 'react';

const SimpleAuthContext = createContext();

export const useSimpleAuth = () => {
  const context = useContext(SimpleAuthContext);
  if (!context) {
    throw new Error('useSimpleAuth must be used within a SimpleAuthProvider');
  }
  return context;
};

export const SimpleAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  // Super simple demo credentials - no backend needed
  const DEMO_USERS = {
    'user': {
      id: 'user-1',
      email: 'user@demo.com',
      name: 'Demo User',
      role: 'user',
      businessType: 'technology'
    },
    'admin': {
      id: 'admin-1', 
      email: 'admin@demo.com',
      name: 'Admin User',
      role: 'admin',
      businessType: 'consulting'
    }
  };

  // Check localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('simpleUser');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  // Super simple login - just check hardcoded credentials
  const login = async (username, password) => {
    setLoading(true);
    
    // Simulate loading
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Check credentials
    if (username === 'user' && password === 'user123') {
      const userData = DEMO_USERS.user;
      setUser(userData);
      localStorage.setItem('simpleUser', JSON.stringify(userData));
      return { success: true, user: userData };
    } 
    else if (username === 'admin' && password === 'admin123') {
      const userData = DEMO_USERS.admin;
      setUser(userData);
      localStorage.setItem('simpleUser', JSON.stringify(userData));
      return { success: true, user: userData };
    }
    else {
      return { success: false, error: 'Invalid credentials. Use: user/user123 or admin/admin123' };
    }
  };

  // Simple logout
  const logout = () => {
    setUser(null);
    localStorage.removeItem('simpleUser');
  };

  // Check if logged in
  const isLoggedIn = () => !!user;

  // Check if admin
  const isAdmin = () => user?.role === 'admin';

  const value = {
    user,
    loading,
    login,
    logout,
    isLoggedIn,
    isAdmin
  };

  return (
    <SimpleAuthContext.Provider value={value}>
      {children}
    </SimpleAuthContext.Provider>
  );
};
